import "@/App.css";
import { BrowserRouter, Route, Routes, useLocation } from "react-router-dom";
import { AuthProvider } from "@/context/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import { useRefTracking } from "@/hooks/useRefTracking";
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import { StudentSignup, AffiliateSignup } from "@/pages/Signup";
import VerifyEmail from "@/pages/VerifyEmail";
import AuthCallback from "@/pages/AuthCallback";
import { StudentDashboard } from "@/pages/Dashboards";
import { Terms, Privacy, RiskDisclosure } from "@/pages/Legal";
import LessonPage from "@/pages/LessonPage";
import QuizPage from "@/pages/QuizPage";
import JournalPage from "@/pages/JournalPage";
import { AdminJournalList, AdminJournalReview } from "@/pages/AdminJournal";
import { BlogList, BlogPost } from "@/pages/Blog";
import AffiliateDashboard from "@/pages/AffiliateDashboard";
import { AffiliateCommissions, AffiliatePayouts } from "@/pages/AffiliateHistory";
import PublicAffiliates from "@/pages/PublicAffiliates";
import LiveSessionsPage from "@/pages/LiveSessionsPage";

// Admin console
import AdminLayout from "@/pages/admin/AdminLayout";
import AdminOverview from "@/pages/admin/AdminOverview";
import AdminCurriculum from "@/pages/admin/AdminCurriculum";
import AdminStudents from "@/pages/admin/AdminStudents";
import AdminStudentDetail from "@/pages/admin/AdminStudentDetail";
import AdminPricing from "@/pages/admin/AdminPricing";
import AdminBlog from "@/pages/admin/AdminBlog";
import AdminAuditLog from "@/pages/admin/AdminAuditLog";
import AdminSettings from "@/pages/admin/AdminSettings";
import AdminAffiliates from "@/pages/admin/AdminAffiliates";
import AdminAffiliateDetail from "@/pages/admin/AdminAffiliateDetail";
import AdminPayouts from "@/pages/admin/AdminPayouts";
import AdminLiveSessions from "@/pages/admin/AdminLiveSessions";
import AdminEmailLog from "@/pages/admin/AdminEmailLog";
import AdminTestimonials from "@/pages/admin/AdminTestimonials";

function OAuthShortCircuit({ children }) {
    const location = useLocation();
    if (location.hash?.includes("session_id=")) return <AuthCallback />;
    return children;
}

function AppRoutes() {
    useRefTracking();
    return (
        <OAuthShortCircuit>
            <Routes>
                <Route path="/" element={<Landing />} />
                <Route path="/login" element={<Login />} />
                <Route path="/signup" element={<StudentSignup />} />
                <Route path="/affiliate/signup" element={<AffiliateSignup />} />
                <Route path="/verify-email" element={<VerifyEmail />} />
                <Route path="/auth/callback" element={<AuthCallback />} />

                {/* Public blog + affiliate info */}
                <Route path="/blog" element={<BlogList />} />
                <Route path="/blog/:slug" element={<BlogPost />} />
                <Route path="/affiliates" element={<PublicAffiliates />} />

                <Route path="/dashboard" element={<ProtectedRoute roles={["student"]}><StudentDashboard /></ProtectedRoute>} />
                <Route path="/learn/lessons/:lessonId" element={<ProtectedRoute roles={["student", "admin"]}><LessonPage /></ProtectedRoute>} />
                <Route path="/learn/quizzes/:quizId" element={<ProtectedRoute roles={["student"]}><QuizPage /></ProtectedRoute>} />
                <Route path="/journal" element={<ProtectedRoute roles={["student"]}><JournalPage /></ProtectedRoute>} />
                <Route path="/live" element={<ProtectedRoute roles={["student", "admin"]}><LiveSessionsPage /></ProtectedRoute>} />

                {/* Affiliate area */}
                <Route path="/affiliate" element={<ProtectedRoute roles={["affiliate"]}><AffiliateDashboard /></ProtectedRoute>} />
                <Route path="/affiliate/commissions" element={<ProtectedRoute roles={["affiliate"]}><AffiliateCommissions /></ProtectedRoute>} />
                <Route path="/affiliate/payouts" element={<ProtectedRoute roles={["affiliate"]}><AffiliatePayouts /></ProtectedRoute>} />

                {/* Admin console */}
                <Route path="/admin" element={<ProtectedRoute roles={["admin"]}><AdminLayout /></ProtectedRoute>}>
                    <Route index element={<AdminOverview />} />
                    <Route path="curriculum" element={<AdminCurriculum />} />
                    <Route path="students" element={<AdminStudents />} />
                    <Route path="students/:userId" element={<AdminStudentDetail />} />
                    <Route path="pricing" element={<AdminPricing />} />
                    <Route path="blog" element={<AdminBlog />} />
                    <Route path="testimonials" element={<AdminTestimonials />} />
                    <Route path="affiliates" element={<AdminAffiliates />} />
                    <Route path="affiliates/:id" element={<AdminAffiliateDetail />} />
                    <Route path="payouts" element={<AdminPayouts />} />
                    <Route path="journal" element={<AdminJournalList />} />
                    <Route path="journal/:entryId" element={<AdminJournalReview />} />
                    <Route path="live-sessions" element={<AdminLiveSessions />} />
                    <Route path="email-log" element={<AdminEmailLog />} />
                    <Route path="audit-log" element={<AdminAuditLog />} />
                    <Route path="settings" element={<AdminSettings />} />
                </Route>

                <Route path="/terms" element={<Terms />} />
                <Route path="/privacy" element={<Privacy />} />
                <Route path="/risk-disclosure" element={<RiskDisclosure />} />

                <Route path="*" element={<Landing />} />
            </Routes>
        </OAuthShortCircuit>
    );
}

export default function App() {
    return (
        <div className="App">
            <BrowserRouter>
                <AuthProvider>
                    <AppRoutes />
                </AuthProvider>
            </BrowserRouter>
        </div>
    );
}
